'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { BOOKING_STORAGE_KEY } from '@/constants/cookie';
import { useClientBrand } from '@/context/client-hostname-provider';
import { confirmOneTimePassword } from '@/services/firebase-admin/confirm-one-time-password';
import { emailOneTimePassword } from '@/services/firebase-admin/email-one-time-password';
import { setVerifiedAccountClaim } from '@/services/firebase-admin/set-verified-account-claim';
import { upsertFirebaseAccount } from '@/services/firebase-admin/upsert-firebase-account';
import {
  upsertFirestoreUser,
  upsertFirestoreUserByAccount,
} from '@/services/firebase-admin/upsert-firestore-user';
import {
  clearRecaptchaVerifier,
  sendPhoneVerificationCode,
  verifyPhoneCode as verifyPhoneCodeService,
} from '@/services/firebase/phone-auth';
import { clearCookie, getCookie } from '@/utils/cookie/cookie-tools';
import { auth, isE2EAuthMode, E2E_FAKE_USER } from '@/utils/firebase-client';
import {
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth';
import { BookingUser } from 'types/auth';

const verboseLogging = false;

type AuthContextProps = {
  logout: () => Promise<void>;
  sendEmailCode: (email: string) => Promise<boolean>;
  verifyEmailCode: (
    email: string,
    code: string,
    phone?: string,
    accountId?: string
  ) => Promise<boolean>;
  sendPhoneCode: (phone: string) => Promise<string | null>;
  verifyPhoneCode: (
    verificationId: string,
    code: string,
    email: string,
    accountId?: string
  ) => Promise<boolean>;
  user: BookingUser | null;
  initialized: boolean;
};

const AuthContext = createContext<AuthContextProps | null>(null);

function AuthContextProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<BookingUser | null>(null);
  const [initialized, setInitialized] = useState(false);
  const { brand } = useClientBrand();

  useEffect(() => {
    // In E2E auth mode, immediately inject a fake logged-in user so the
    // booking page never shows the OTP modal.
    if (isE2EAuthMode()) {
      const fakeUser = E2E_FAKE_USER;
      setUser({
        uid: fakeUser.uid,
        email: fakeUser.email as string,
        displayName: fakeUser.displayName as string,
        phone: null,
        providers: [],
      });
      setInitialized(true);
      return;
    }

    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        if (!user) {
          verboseLogging && console.log('user is logged out');
          setUser(null);
          setInitialized(true);
          return;
        }
        verboseLogging && console.log('user logged in', user);
        const signedInUser: BookingUser = {
          uid: user.uid,
          email: user.email as string,
          displayName: user.displayName as string,
          phone: user.phoneNumber,
          providers: user.providerData,
        };

        const storedBookingCookie = getCookie(BOOKING_STORAGE_KEY);
        if (
          storedBookingCookie &&
          typeof storedBookingCookie === 'object' &&
          storedBookingCookie.email !== signedInUser.email
        ) {
          // clear the booking cookie on change of user, to prevent bad confirmation pages
          clearCookie(BOOKING_STORAGE_KEY);
        }

        setUser(signedInUser);
        setInitialized(true);
      },
      (error) => {
        console.error('onAuthStateChanged error', error);
      }
    );
    return () => {
      setInitialized(false);
      unsubscribe();
    };
  }, []);

  // RecaptchaVerifier must be cleared on unmount so a later checkout visit
  // can render into a fresh DOM node (clear() does not unmount invisible widgets).
  useEffect(() => {
    return () => {
      clearRecaptchaVerifier();
    };
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth)
      .then(() => {
        verboseLogging && console.log('Signed out.');
      })
      .catch((error) => {
        console.error('Sign out error', error);
      });
  }, []);

  const sendEmailCode = useCallback(
    async (email: string) => {
      const firebaseAccount = await upsertFirebaseAccount(email);
      if (!firebaseAccount) {
        return false;
      }
      // Create Firestore user for OTP storage (server-side only, safe)
      const firestoreUser = await upsertFirestoreUserByAccount(firebaseAccount);
      if (!firestoreUser) {
        return false;
      }
      return await emailOneTimePassword(email, brand.key);
    },
    [brand]
  );

  const verifyEmailCode = useCallback(
    async (email: string, code: string, phone?: string) => {
      return await confirmOneTimePassword(email, code).then(
        async (confirmOTPResult) => {
          if (confirmOTPResult.success) {
            await signInWithCustomToken(auth, confirmOTPResult.customToken);

            // After email verification, create/update Firestore with both credentials
            const currentUser = auth.currentUser;
            if (currentUser) {
              // Best-effort: sync phone to Firestore. Non-critical — don't block auth on failure.
              try {
                const idToken = await currentUser.getIdToken();
                const firestoreUser = await upsertFirestoreUser(
                  idToken,
                  email.toLowerCase(),
                  phone
                );
                if (!firestoreUser) {
                  console.warn(
                    'Failed to create Firestore user after email verification'
                  );
                }
              } catch (error) {
                console.error('Error syncing Firestore user after email verification:', error);
              }

              // Critical: set account_id claim and wait for it to propagate to the client token.
              // If this fails the user will hit a 401 on every Salesforce call, so we surface
              // the failure here instead of letting it produce a confusing downstream error.
              try {
                const { setVerifiedAccountClaim } = await import(
                  '@/services/firebase-admin/set-verified-account-claim'
                );
                await setVerifiedAccountClaim(
                  currentUser.uid,
                  email.toLowerCase(),
                  phone // Pass phone from form - Firebase Auth won't have it for email verification
                );

                // Firebase Admin setCustomUserClaims takes effect server-side immediately,
                // but the cached client JWT won't reflect it until the next forced refresh.
                const { waitForFirebaseIdTokenWithAccountClaim } = await import(
                  '@/utils/wait-for-account-id-claim'
                );
                const tokenWithClaim = await waitForFirebaseIdTokenWithAccountClaim();
                if (!tokenWithClaim) {
                  throw new Error('account_id claim did not propagate to token within timeout');
                }
              } catch (error) {
                console.error(
                  'Error setting account_id claim after email verification:',
                  error
                );
                return false;
              }
            }

            return true;
          }
          return false;
        }
      );
    },
    []
  );

  const sendPhoneCode = useCallback(async (phone: string) => {
    try {
      return await sendPhoneVerificationCode(phone);
    } catch (error) {
      console.error('Error in sendPhoneCode:', error);
      // Re-throw rate-limit so the guest auth UI can show a clear message
      if (
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        (error as { code: string }).code === 'auth/too-many-requests'
      ) {
        throw error;
      }
      return null;
    }
  }, []);

  const verifyPhoneCode = useCallback(
    async (verificationId: string, code: string, email: string) => {
      const result = await verifyPhoneCodeService(verificationId, code);
      if (result) {
        // After successful phone verification, update Firestore and set claim
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.uid) {
          try {
            // Create/update Firestore user with both email and phone
            // Note: Firebase Auth has ONLY phone (single provider approach)
            // Email is stored in Firestore just for record keeping
            // SECURITY: Pass ID token, not UID, to verify user identity
            const idToken = await currentUser.getIdToken();
            const firestoreUser = await upsertFirestoreUser(
              idToken,
              email.toLowerCase(),
              currentUser.phoneNumber || undefined
            );
            if (!firestoreUser) {
              console.warn(
                'Failed to create Firestore user after phone verification'
              );
            }

            // Critical: set account_id claim and wait for it to propagate to the client token.
            // If this fails the user will hit a 401 on every Salesforce call, so we surface
            // the failure here instead of letting it produce a confusing downstream error.
            await setVerifiedAccountClaim(
              currentUser.uid,
              email.toLowerCase(),
              undefined // Phone verification: server will use Firebase Auth's verified phone
            );

            const { waitForFirebaseIdTokenWithAccountClaim } = await import(
              '@/utils/wait-for-account-id-claim'
            );
            const tokenWithClaim = await waitForFirebaseIdTokenWithAccountClaim();
            if (!tokenWithClaim) {
              throw new Error('account_id claim did not propagate to token within timeout');
            }
          } catch (error) {
            console.error(
              'Error setting account_id claim after phone verification:',
              error
            );
            return false;
          }
        }
      }
      return result;
    },
    []
  );

  const memoizedValue = useMemo(
    () => ({
      logout,
      user,
      sendEmailCode,
      verifyEmailCode,
      sendPhoneCode,
      verifyPhoneCode,
      initialized,
    }),
    [
      user,
      logout,
      sendEmailCode,
      verifyEmailCode,
      sendPhoneCode,
      verifyPhoneCode,
      initialized,
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      'useAuthContext must be called inside of AuthContextProvider.'
    );
  }
  return context;
}

export default AuthContextProvider;
